/**
 * RFM (Recency, Frequency, Monetary) Analysis Constants
 *
 * This file defines the standard 11-segment model with fixed score boundaries,
 * actionable recommendations, and UI metadata for the RFM customer segmentation feature.
 */

import type { RFMSegment, SegmentDefinition } from '../types/rfm-analytics.js';

/**
 * Analysis period duration in days
 * RFM scores are calculated using the last 18 months (540 days) of transaction history
 */
export const RFM_ANALYSIS_PERIOD_DAYS = 540 as const;

/**
 * Decile scale range
 * All RFM components (R, F, M) are scored on a 1-10 scale
 */
export const RFM_SCORE_MIN = 1 as const;
export const RFM_SCORE_MAX = 10 as const;

/**
 * Default limits for API responses
 */
export const DEFAULT_CUSTOMERS_PER_SEGMENT = 100 as const;
export const MAX_CUSTOMERS_PER_SEGMENT = 1000 as const;
export const DEFAULT_PAGINATION_LIMIT = 50 as const;
export const MAX_PAGINATION_LIMIT = 500 as const;

/**
 * Segment definitions with complete metadata and business rules
 * Each segment includes:
 * - UI metadata (display name, description, color, icon)
 * - Business priority level
 * - Actionable recommendations
 * - RFM score ranges for classification
 */
export const SEGMENT_DEFINITIONS: Record<RFMSegment, SegmentDefinition> = {
  "Champions": {
    segment: "Champions",
    displayName: "แชมเปี้ยน (Champions)",
    description: "ลูกค้าที่ดีที่สุดที่ซื้อบ่อย ล่าสุด และใช้จ่ายมากที่สุด",
    color: "bg-green-600",
    icon: "Trophy",
    priority: "Critical",
    recommendations: [
      "มอบรางวัลความภักดีด้วยสิทธิพิเศษและบริการ VIP",
      "ขอรีวิว คำรับรองและการแนะนำลูกค้า",
      "เสนอสิทธิ์เข้าถึงสินค้าและบริการใหม่ล่วงหน้า",
      "รักษาการสื่อสารส่วนตัวและความสัมพันธ์",
      "พิจารณาบริการระดับพรีเมียม/คอนเซียร์จ"
    ],
    scoreRanges: {
      recency: { min: 9, max: 10 },
      frequency: { min: 9, max: 10 },
      monetary: { min: 9, max: 10 }
    }
  },

  "Loyal Customers": {
    segment: "Loyal Customers",
    displayName: "ลูกค้าภักดี (Loyal Customers)",
    description: "ลูกค้าประจำที่มีการมีส่วนร่วมสม่ำเสมอและมีมูลค่าสูง",
    color: "bg-blue-600",
    icon: "Heart",
    priority: "High",
    recommendations: [
      "เสนอขายสินค้าพรีเมียมและสินค้าเสริม",
      "เชิญเข้าร่วมโปรแกรมความภักดีหรือรางวัล",
      "แนะนำบริการเสริมและส่วนเพิ่ม",
      "มอบสิทธิประโยชน์สมาชิกพิเศษ",
      "แสดงความขอบคุณและชื่นชมความภักดี"
    ],
    scoreRanges: {
      recency: { min: 5, max: 10 },  // Expanded from 7 to include medium-recency loyal customers
      frequency: { min: 7, max: 10 },
      monetary: { min: 1, max: 10 }  // Expanded to capture ALL high-frequency customers regardless of spending
    }
  },

  "Potential Loyalists": {
    segment: "Potential Loyalists",
    displayName: "ลูกค้าที่มีศักยภาพ (Potential Loyalists)",
    description: "ลูกค้าที่ซื้อล่าสุดและบ่อยที่มีศักยภาพการใช้จ่ายที่เพิ่มขึ้น",
    color: "bg-cyan-600",
    icon: "TrendingUp",
    priority: "High",
    recommendations: [
      "เสริมสร้างความสัมพันธ์ด้วยข้อเสนอส่วนตัว",
      "แนะนำสายผลิตภัณฑ์พรีเมียม",
      "มอบประสบการณ์บริการลูกค้าที่ยอดเยี่ยม",
      "สร้างโอกาสขายเพิ่มด้วยบันเดิล",
      "กระตุ้นให้ซื้อสินค้ามูลค่าสูงขึ้นด้วยแรงจูงใจ"
    ],
    scoreRanges: {
      recency: { min: 5, max: 10 },  // Expanded from 7 to match Loyal Customers expansion
      frequency: { min: 4, max: 6 },
      monetary: { min: 1, max: 10 }  // Expanded to capture all medium-frequency recent customers regardless of spending
    }
  },

  "New Customers": {
    segment: "New Customers",
    displayName: "ลูกค้าใหม่ (New Customers)",
    description: "ลูกค้าที่เพิ่งได้มาซึ่งต้องการการดูแลและการต้อนรับ",
    color: "bg-green-500",
    icon: "UserPlus",
    priority: "Medium",
    recommendations: [
      "มอบประสบการณ์การเริ่มต้นที่ยอดเยี่ยม",
      "ส่งชุดต้อนรับและการศึกษาผลิตภัณฑ์",
      "เสนอแรงจูงใจสำหรับลูกค้าใหม่เพื่อซื้อซ้ำ",
      "รวบรวมคำติชมเกี่ยวกับประสบการณ์การซื้อครั้งแรก",
      "สร้างรากฐานความสัมพันธ์ด้วยการสื่อสารส่วนตัว"
    ],
    scoreRanges: {
      recency: { min: 9, max: 10 },
      frequency: { min: 3, max: 3 },  // F=3 represents first-time buyers (1 purchase) due to tie-handling
      monetary: { min: 1, max: 10 }  // Accept any monetary value for new customers
    }
  },

  "Promising": {
    segment: "Promising",
    displayName: "น่าจับตามอง (Promising)",
    description: "ลูกค้าที่ซื้อล่าสุดที่มีศักยภาพในการเติบโตและมีส่วนร่วมเพิ่มขึ้น",
    color: "bg-lime-600",
    icon: "Sparkles",
    priority: "Medium",
    recommendations: [
      "สร้างแคมเปญเฉพาะเพื่อเพิ่มความถี่",
      "แนะนำผลิตภัณฑ์ตามการซื้อครั้งแรก",
      "เสนอแรงจูงใจสำหรับการซื้อครั้งที่สองและสาม",
      "สร้างการมีส่วนร่วมผ่านเนื้อหาเชิงการศึกษา",
      "ติดตามและปรับปรุงช่องทางการแปลง"
    ],
    scoreRanges: {
      recency: { min: 7, max: 10 },
      frequency: { min: 4, max: 5 },  // F=4-5 represents 2-3 purchases (early adopters)
      monetary: { min: 1, max: 10 }   // Accept any monetary value for recent low-frequency buyers
    }
  },

  "Need Attention": {
    segment: "Need Attention",
    displayName: "ต้องให้ความสนใจ (Need Attention)",
    description: "ลูกค้าที่แสดงสัญญาณการมีส่วนร่วมที่ลดลง",
    color: "bg-yellow-600",
    icon: "AlertCircle",
    priority: "Medium",
    recommendations: [
      "มีส่วนร่วมใหม่ด้วยข้อเสนอส่งเสริมการขายเฉพาะ",
      "สำรวจเพื่อทำความเข้าใจความพึงพอใจและข้อกังวล",
      "เน้นผลิตภัณฑ์ใหม่หรือการปรับปรุง",
      "มอบแรงจูงใจที่จำกัดเวลา",
      "ตรวจสอบและแก้ไขปัญหาการบริการใดๆ"
    ],
    scoreRanges: {
      recency: { min: 5, max: 6 },
      frequency: { min: 4, max: 6 },
      monetary: { min: 3, max: 7 }  // Wider range to capture more medium-tier customers
    }
  },

  "About to Sleep": {
    segment: "About to Sleep",
    displayName: "ใกล้หยุดนิ่ง (About to Sleep)",
    description: "ลูกค้าที่ไม่ได้ซื้อเมื่อเร็วๆ นี้และอาจไม่ใช้งาน",
    color: "bg-orange-500",
    icon: "Moon",
    priority: "Low",
    recommendations: [
      "ส่งแคมเปญกลับมามีส่วนร่วมด้วยข้อเสนอที่น่าสนใจ",
      "เตือนเกี่ยวกับผลประโยชน์หรือบริการที่ไม่ได้ใช้",
      "แบ่งปันการเปิดตัวผลิตภัณฑ์ใหม่และการอัปเดต",
      "เสนอส่วนลดการเปิดใช้งานใหม่",
      "ใช้กลยุทธ์ความเร่งด่วนและความหายาก"
    ],
    scoreRanges: {
      recency: { min: 3, max: 6 },
      frequency: { min: 3, max: 4 },  // Adjusted from 1 to 3 to match actual data (min F=3)
      monetary: { min: 3, max: 6 }
    }
  },

  "At Risk": {
    segment: "At Risk",
    displayName: "เสี่ยงสูญเสีย (At Risk)",
    description: "ลูกค้าที่เคยดีที่กำลังสูญเสียการมีส่วนร่วมและอาจออกไป",
    color: "bg-orange-600",
    icon: "AlertTriangle",
    priority: "Critical",
    recommendations: [
      "แคมเปญชนะคืนทันทีด้วยข้อเสนอที่แข็งแกร่ง",
      "ติดต่อส่วนบุคคลจากผู้จัดการบัญชีหรือทีมขาย",
      "ดำเนินการสัมภาษณ์ออกเพื่อทำความเข้าใจปัญหา",
      "เสนอแรงจูงใจการเปิดใช้งานใหม่ที่สำคัญ",
      "จัดการปัญหาการบริการหรือข้อกังวลอย่างเชิงรุก",
      "พิจารณาการกำหนดราคาพิเศษหรือข้อตกลงแพ็คเกจ"
    ],
    scoreRanges: {
      recency: { min: 1, max: 4 },
      frequency: { min: 4, max: 6 },
      monetary: { min: 6, max: 8 }
    }
  },

  "Can't Lose Them": {
    segment: "Can't Lose Them",
    displayName: "ห้ามสูญเสีย (Can't Lose Them)",
    description: "ลูกค้ามูลค่าสูงที่ไม่ได้ซื้อเมื่อเร็วๆ นี้ (สำคัญต่อการรักษา)",
    color: "bg-red-600",
    icon: "ShieldAlert",
    priority: "Critical",
    recommendations: [
      "ด่วน: ติดต่อส่วนบุคคลระดับผู้บริหารทันที",
      "สืบสวนและแก้ไขปัญหาความไม่พอใจใดๆ",
      "เสนอแพ็คเกจเปิดใช้งาน VIP พิเศษ",
      "มอบการจัดการบัญชีโดยเฉพาะ",
      "ข้อเสนอโซลูชันที่กำหนดเองสำหรับความต้องการของพวกเขา",
      "พิจารณารางวัลความภักดีหรือเครดิตที่สำคัญ"
    ],
    scoreRanges: {
      recency: { min: 1, max: 4 },
      frequency: { min: 7, max: 10 },
      monetary: { min: 7, max: 10 }
    }
  },

  "Hibernating": {
    segment: "Hibernating",
    displayName: "หยุดนิ่ง (Hibernating)",
    description: "ลูกค้าที่ไม่ใช้งานมานานโดยมีมูลค่าประวัติต่ำ",
    color: "bg-gray-600",
    icon: "Cloud",
    priority: "Low",
    recommendations: [
      "แคมเปญกลับมามีส่วนร่วมต้นทุนต่ำ",
      "แคมเปญอีเมลด้วยข้อเสนอใหม่ที่น่าสนใจ",
      "พิจารณาลบออกจากการตลาดที่ใช้งานอยู่หากไม่ตอบสนอง",
      "แคมเปญพระอาทิตย์ตกด้วยข้อเสนอสุดท้าย",
      "เก็บถาวรหลังจากความพยายามเปิดใช้งานใหม่ที่ล้มเหลวหลายครั้ง"
    ],
    scoreRanges: {
      recency: { min: 1, max: 4 },
      frequency: { min: 3, max: 4 },  // Adjusted from 1 to 3 to match actual data (min F=3)
      monetary: { min: 3, max: 6 }
    }
  },

  "Lost": {
    segment: "Lost",
    displayName: "สูญเสียแล้ว (Lost)",
    description: "ลูกค้าที่ไม่ได้ซื้อมาเป็นเวลานานมาก (อาจออกไปแล้ว)",
    color: "bg-gray-800",
    icon: "XCircle",
    priority: "Low",
    recommendations: [
      "แคมเปญพระอาทิตย์ตกสุดท้ายด้วยข้อเสนอโอกาสสุดท้าย",
      "ลบออกจากรายการการตลาดปกติเพื่อลดต้นทุน",
      "เก็บถาวรข้อมูลลูกค้าตามนโยบายการเก็บรักษา",
      "วิเคราะห์รูปแบบการออกเพื่อป้องกันการสูญเสียในอนาคต",
      "มุ่งเน้นทรัพยากรไปยังกลุ่มที่มีความสำคัญสูงกว่า"
    ],
    scoreRanges: {
      recency: { min: 1, max: 2 },
      frequency: { min: 3, max: 3 },  // Adjusted to F=3 to match actual data (min F=3)
      monetary: { min: 1, max: 2 }
    }
  }
} as const;

/**
 * Array of all segment names in priority order
 * Critical segments first, then High, Medium, Low
 */
export const SEGMENT_PRIORITY_ORDER: RFMSegment[] = [
  // Critical priority
  "Champions",
  "At Risk",
  "Can't Lose Them",
  // High priority
  "Loyal Customers",
  "Potential Loyalists",
  // Medium priority
  "New Customers",
  "Promising",
  "Need Attention",
  // Low priority
  "About to Sleep",
  "Hibernating",
  "Lost"
] as const;

/**
 * Segment color mapping for chart visualization
 * Uses Tailwind color names without 'bg-' prefix for flexibility
 */
export const SEGMENT_COLORS: Record<RFMSegment, string> = {
  "Champions": "green-600",
  "Loyal Customers": "blue-600",
  "Potential Loyalists": "cyan-600",
  "New Customers": "green-500",
  "Promising": "lime-600",
  "Need Attention": "yellow-600",
  "About to Sleep": "orange-500",
  "At Risk": "orange-600",
  "Can't Lose Them": "red-600",
  "Hibernating": "gray-600",
  "Lost": "gray-800"
} as const;

/**
 * Helper function to get segment definition
 */
export function getSegmentDefinition(segment: RFMSegment): SegmentDefinition {
  return SEGMENT_DEFINITIONS[segment];
}

/**
 * Helper function to check if a score falls within a segment's range
 */
export function isScoreInSegmentRange(
  r: number,
  f: number,
  m: number,
  segment: RFMSegment
): boolean {
  const def = SEGMENT_DEFINITIONS[segment];
  return (
    r >= def.scoreRanges.recency.min && r <= def.scoreRanges.recency.max &&
    f >= def.scoreRanges.frequency.min && f <= def.scoreRanges.frequency.max &&
    m >= def.scoreRanges.monetary.min && m <= def.scoreRanges.monetary.max
  );
}

/**
 * Helper function to get all segments in priority order
 */
export function getSegmentsByPriority(): RFMSegment[] {
  return [...SEGMENT_PRIORITY_ORDER];
}

/**
 * Helper function to get segments by priority level
 */
export function getSegmentsByPriorityLevel(
  level: "Critical" | "High" | "Medium" | "Low"
): RFMSegment[] {
  return Object.values(SEGMENT_DEFINITIONS)
    .filter(def => def.priority === level)
    .map(def => def.segment);
}
